import { Alert, App, Button, Form, Input, Select, Space, Tag } from 'antd';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { http } from '../../api/client.js';
import { PlatformRoleMembersPanel } from './PlatformRoleMembersPanel.js';
import type { PlatformRoleDetail, PlatformRoleType } from './platform-permission-types.js';
import { isPlatformRoleType } from './platform-permission-types.js';

interface PlatformRoleGeneralFormValues {
  name: string;
  title?: string;
  description?: string;
  roleType: PlatformRoleType;
}

const ROLE_TYPE_KEYS: PlatformRoleType[] = ['user', 'admin', 'system'];

function toFormValues(detail: PlatformRoleDetail): PlatformRoleGeneralFormValues {
  return {
    name: detail.name,
    title: detail.title ?? '',
    description: detail.description ?? '',
    roleType: isPlatformRoleType(detail.roleType) ? detail.roleType : 'user',
  };
}

function extractErrorMessage(error: unknown): string | null {
  const response = (error as { response?: { data?: { message?: unknown } } })?.response;
  return typeof response?.data?.message === 'string' ? response.data.message : null;
}

export function PlatformRoleGeneralPanel({
  detail,
  onChanged,
}: {
  detail: PlatformRoleDetail;
  onChanged: () => Promise<void> | void;
}) {
  const { message } = App.useApp();
  const { t } = useTranslation('platformPermissions');
  const [form] = Form.useForm<PlatformRoleGeneralFormValues>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    form.setFieldsValue(toFormValues(detail));
  }, [detail, form]);

  const saveGeneral = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      await http.patch<PlatformRoleDetail>(`/admin/platform-roles/${detail.roleid}`, {
        name: values.name.trim(),
        title: values.title?.trim() || null,
        description: values.description?.trim() || null,
        roleType: values.roleType,
      });
      message.success(t('roleInfoSaved'));
      await onChanged();
    } catch (error) {
      message.error(extractErrorMessage(error) ?? t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Alert type="info" showIcon message={t('boundaryTitle')} description={t('boundaryDesc')} />
      <Form form={form} layout="vertical" initialValues={toFormValues(detail)}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
          }}
        >
          <Form.Item
            label={t('roleName')}
            name="name"
            rules={[
              { required: true, whitespace: true, message: t('roleNameRequired') },
              { max: 100, message: t('roleNameMax') },
            ]}
          >
            <Input placeholder={t('roleNamePlaceholder')} allowClear />
          </Form.Item>
          <Form.Item
            label={t('roleType')}
            name="roleType"
            rules={[{ required: true, message: t('selectRoleType') }]}
          >
            <Select
              options={ROLE_TYPE_KEYS.map((value) => ({ value, label: t(value) }))}
              popupMatchSelectWidth={false}
            />
          </Form.Item>
        </div>

        <Form.Item label={t('displayName')} name="title">
          <Input placeholder={t('displayNamePlaceholder')} allowClear />
        </Form.Item>

        <Form.Item label={t('description')} name="description">
          <Input.TextArea rows={3} placeholder={t('descriptionPlaceholder')} allowClear />
        </Form.Item>

        <Space wrap style={{ marginBottom: 12 }}>
          <Tag color="blue">
            {t('members')}: {detail.memberCount}
          </Tag>
          <Tag color="purple">
            {t('privileges')}: {detail.privilegeCount}
          </Tag>
          <Tag color="cyan">
            {t('appAccess')}: {detail.appCount}
          </Tag>
        </Space>

        <div>
          <Button type="primary" icon={<Save size={14} />} loading={saving} onClick={saveGeneral}>
            {t('saveRoleInfo')}
          </Button>
        </div>
      </Form>
      <PlatformRoleMembersPanel detail={detail} onChanged={onChanged} />
    </Space>
  );
}
